import { useState, useEffect, useRef } from 'react';
import { useAuth, API } from '../../App';
import { useDateTimeFormat } from '../../hooks/useDateTimeFormat';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, TrendingUp, TrendingDown, DollarSign, Eye, FileImage, Loader2, ChevronLeft, ChevronRight, FileText, User, Car, Download, ExternalLink, MapPin, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


export default function Expenses() {
  const { token, user } = useAuth();
  const { formatDateTime } = useDateTimeFormat();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [ridesWithCommissions, setRidesWithCommissions] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Month/Year filter state (matching Company Financials)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Driver and Vehicle filters
  const [filterDriver, setFilterDriver] = useState('all');
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterCommissionTo, setFilterCommissionTo] = useState('all'); // Filter by commission recipient
  
  // Net total calculation mode - Load from localStorage with user-specific key
  const getStoredCalculationMode = () => {
    if (user?.id) {
      const stored = localStorage.getItem(`expenses_calc_mode_${user.id}`);
      return stored || 'subtract';
    }
    return 'subtract';
  };
  const [netCalculationMode, setNetCalculationMode] = useState(getStoredCalculationMode);
  
  // Save calculation mode preference when it changes
  const handleCalculationModeChange = (mode) => {
    setNetCalculationMode(mode);
    if (user?.id) {
      localStorage.setItem(`expenses_calc_mode_${user.id}`, mode);
    }
  };
  
  // Load saved preference when user changes
  useEffect(() => {
    if (user?.id) {
      const stored = localStorage.getItem(`expenses_calc_mode_${user.id}`);
      if (stored) {
        setNetCalculationMode(stored);
      }
    }
  }, [user?.id]);
  
  const [formData, setFormData] = useState({
    amount: '',
    date: '',
    reason: '',
    driver_id: '',
    vehicle_id: '',
    receipt_data: null,
    receipt_filename: null
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  useEffect(() => {
    fetchDrivers();
    fetchVehicles();
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchSummary();
    fetchRidesWithCommissions();
  }, [selectedYear, selectedMonth, filterDriver, filterVehicle, filterCommissionTo]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const driversList = response.data.filter(user => user.role === 'driver' || user.role === 'admin-driver');
      setDrivers(driversList);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams({ 
        period: 'month',
        year: selectedYear.toString(),
        month: selectedMonth.toString()
      });
      if (filterDriver !== 'all') {
        params.append('driver_id', filterDriver);
      }
      if (filterVehicle !== 'all') {
        params.append('vehicle_id', filterVehicle);
      }
      
      const response = await axios.get(`${API}/expenses/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchRidesWithCommissions = async () => {
    try {
      // Server-side filter: only rides with driver_commission > 0.
      // This is critical in production where a month can have >>500 rides;
      // without it, paginating through all rides was needed and could miss
      // commissions on drivers whose rides fell past the page_size.
      // If a specific driver/recipient is chosen, filter server-side as well
      // so we cover both assigned_driver_id and commission_driver_id.
      const pastParams = {
        month: selectedMonth,
        year: selectedYear,
        page_size: 500,
        page: 1,
        has_commission: true,
      };
      if (filterDriver !== 'all') {
        pastParams.commission_recipient_id = filterDriver;
      } else if (filterCommissionTo !== 'all') {
        pastParams.commission_recipient_id = filterCommissionTo;
      }

      // Fetch first page + drain remaining pages so nothing is silently truncated
      const firstPastRes = await axios.get(`${API}/rides/past`, {
        headers: { Authorization: `Bearer ${token}` },
        params: pastParams,
      });
      let pastRides = firstPastRes.data.rides || [];
      const totalPages = firstPastRes.data.total_pages || 1;
      if (totalPages > 1) {
        const extraPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            axios.get(`${API}/rides/past`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { ...pastParams, page: i + 2 },
            }).then(r => r.data.rides || [])
          )
        );
        for (const p of extraPages) pastRides = pastRides.concat(p);
      }

      // Also pull today/future rides that already have a commission (rare but valid)
      const currentResponse = await axios.get(`${API}/rides`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_past: false }
      });
      const currentRides = currentResponse.data || [];

      // Combine and deduplicate rides
      const allRidesMap = new Map();
      [...pastRides, ...currentRides].forEach(ride => {
        if (!allRidesMap.has(ride.id)) {
          allRidesMap.set(ride.id, ride);
        }
      });

      // Client-side filter for month/year (safety net for today/future rides)
      // and for the UI filters that still work on the aggregated set.
      const ridesFiltered = Array.from(allRidesMap.values()).filter(ride => {
        if (!ride.driver_commission || ride.driver_commission <= 0) return false;

        // Filter by date to match selected month/year
        if (ride.date_time) {
          const rideDate = new Date(ride.date_time);
          if (rideDate.getMonth() + 1 !== selectedMonth || rideDate.getFullYear() !== selectedYear) {
            return false;
          }
        }

        // Determine who receives the commission
        const commissionRecipient = ride.commission_driver_id || ride.assigned_driver_id;

        // Filter by assigned driver if selected — a driver is considered
        // "involved" if they're either the assigned driver OR the commission
        // recipient (previously we only checked assigned_driver_id which hid
        // rides where the commission was routed to a different driver).
        if (filterDriver !== 'all') {
          if (ride.assigned_driver_id !== filterDriver && commissionRecipient !== filterDriver) {
            return false;
          }
        }

        // Filter by commission recipient if selected
        if (filterCommissionTo !== 'all') {
          if (commissionRecipient !== filterCommissionTo) {
            return false;
          }
        }

        return true;
      });

      // Sort by date descending
      ridesFiltered.sort((a, b) => new Date(b.date_time) - new Date(a.date_time));

      setRidesWithCommissions(ridesFiltered);
    } catch (error) {
      console.error('Error fetching rides with commissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        amount: formData.amount ? parseFloat(formData.amount) : null,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        reason: formData.reason || null,
        driver_id: formData.driver_id || null,
        vehicle_id: formData.vehicle_id || null,
        receipt_data: formData.receipt_data,
        receipt_filename: formData.receipt_filename
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API}/expenses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Expense added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/expenses/${expenseToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense deleted successfully');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    const expenseDate = expense.date ? new Date(expense.date) : null;
    const localDateTime = expenseDate 
      ? new Date(expenseDate.getTime() - expenseDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      : '';
    
    setFormData({
      amount: expense.amount?.toString() || '',
      date: localDateTime,
      reason: expense.reason || '',
      driver_id: expense.driver_id || '',
      vehicle_id: expense.vehicle_id || '',
      receipt_data: expense.receipt_data || null,
      receipt_filename: expense.receipt_filename || null
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      date: '',
      reason: '',
      driver_id: '',
      vehicle_id: '',
      receipt_data: null,
      receipt_filename: null
    });
    setEditingExpense(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          receipt_data: reader.result,
          receipt_filename: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const viewReceipt = async (expense) => {
    if (!expense) return;
    try {
      const res = await axios.get(`${API}/expenses/${expense.id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data?.receipt_data;
      if (!data) {
        toast.error('No receipt available');
        return;
      }
      setSelectedReceipt({ data, filename: res.data?.receipt_filename || expense.receipt_filename });
      setShowReceiptModal(true);
    } catch (err) {
      console.error('Error fetching receipt:', err);
      toast.error('Failed to load receipt');
    }
  };

  const downloadReceipt = async (expense) => {
    if (!expense) return;
    try {
      const res = await axios.get(`${API}/expenses/${expense.id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data?.receipt_data;
      if (!data) {
        toast.error('No receipt available');
        return;
      }
      downloadFile(data, res.data?.receipt_filename || expense.receipt_filename || 'receipt');
    } catch (err) {
      console.error('Error downloading receipt:', err);
      toast.error('Failed to download receipt');
    }
  };

  const downloadFile = (fileData, fileName) => {
    if (!fileData) return;
    
    const link = document.createElement('a');
    link.href = fileData;
    link.download = fileName || 'receipt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  // Filter expenses by month/year, driver/vehicle, and search
  const filteredExpenses = expenses.filter(expense => {
    // Filter by month/year
    if (expense.date) {
      const expenseDate = new Date(expense.date);
      if (expenseDate.getFullYear() !== selectedYear || expenseDate.getMonth() + 1 !== selectedMonth) {
        return false;
      }
    }
    // Apply driver filter
    if (filterDriver !== 'all' && expense.driver_id !== filterDriver) {
      return false;
    }
    // Apply vehicle filter
    if (filterVehicle !== 'all' && expense.vehicle_id !== filterVehicle) {
      return false;
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesReason = expense.reason && expense.reason.toLowerCase().includes(query);
      const matchesDriver = expense.driver_name && expense.driver_name.toLowerCase().includes(query);
      const matchesVehicle = expense.vehicle_plate && expense.vehicle_plate.toLowerCase().includes(query);
      const matchesAmount = expense.amount && expense.amount.toString().includes(query);
      if (!matchesReason && !matchesDriver && !matchesVehicle && !matchesAmount) {
        return false;
      }
    }
    return true;
  });

  // Filter commissions by search
  const filteredCommissions = ridesWithCommissions.filter(ride => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (ride.customer_name && ride.customer_name.toLowerCase().includes(query)) ||
      (ride.pickup_location && ride.pickup_location.toLowerCase().includes(query)) ||
      (ride.dropoff_location && ride.dropoff_location.toLowerCase().includes(query)) ||
      (ride.assigned_driver_name && ride.assigned_driver_name.toLowerCase().includes(query)) ||
      (ride.driver_commission && ride.driver_commission.toString().includes(query)) ||
      (ride.id && ride.id.toLowerCase().includes(query))
    );
  });

  // Calculate totals for the current view - now uses month-filtered summary from backend
  const totalExpenses = summary?.total_expenses || 0;
  const totalCommission = summary?.total_commission || 0;
  const netTotal = netCalculationMode === 'subtract' 
    ? (totalCommission - totalExpenses)
    : (totalCommission + totalExpenses);

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Expenses Management</h1>
          <p className="text-sm lg:text-base text-slate-600 mt-1">Track driver expenses and commissions</p>
        </div>
      </div>

      {/* Month/Year Selector */}
      <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm shadow-sm p-4 mb-6">
        <div className="flex items-center justify-center gap-2 lg:gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth('prev')}
            data-testid="prev-month-btn"
            className="h-10 w-10 lg:h-9 lg:w-9"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-2 lg:gap-3">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[120px] lg:w-[140px] h-10 lg:h-9" data-testid="month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[90px] lg:w-[100px] h-10 lg:h-9" data-testid="year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth('next')}
            data-testid="next-month-btn"
            className="h-10 w-10 lg:h-9 lg:w-9"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-slate-500" />
            <Label className="text-sm text-slate-600">Assigned Driver:</Label>
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger className="w-[180px]" data-testid="expense-filter-driver">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map(driver => (
                  <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            <Label className="text-sm text-slate-600">Commission To:</Label>
            <Select value={filterCommissionTo} onValueChange={setFilterCommissionTo}>
              <SelectTrigger className="w-[180px]" data-testid="expense-filter-commission-to">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Recipients</SelectItem>
                {drivers.map(driver => (
                  <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Car className="h-4 w-4 text-slate-500" />
            <Label className="text-sm text-slate-600">Vehicle:</Label>
            <Select value={filterVehicle} onValueChange={setFilterVehicle}>
              <SelectTrigger className="w-[180px]" data-testid="expense-filter-vehicle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vehicles</SelectItem>
                {vehicles.map(vehicle => (
                  <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.plate_number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(filterDriver !== 'all' || filterVehicle !== 'all' || filterCommissionTo !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFilterDriver('all'); setFilterVehicle('all'); setFilterCommissionTo('all'); }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear Filters
            </Button>
          )}
        </div>
        
        {/* Search Input */}
        <div className="flex items-center gap-2 mt-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by reason, driver, vehicle, amount..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="expense-search-input"
            />
          </div>
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchQuery('')}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-100 rounded-sm">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Total Commission</span>
          </div>
          <p className="text-3xl font-bold text-emerald-600" data-testid="total-commission">
            {formatCurrency(totalCommission)}
          </p>
          <p className="text-sm text-slate-500 mt-1">From completed rides</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-100 rounded-sm">
              <TrendingDown className="h-5 w-5 text-red-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Total Expenses</span>
          </div>
          <p className="text-3xl font-bold text-red-600" data-testid="total-expenses">
            {formatCurrency(totalExpenses)}
          </p>
          <p className="text-sm text-slate-500 mt-1">{filteredExpenses.length} entries</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-sm ${netTotal >= 0 ? 'bg-sky-100' : 'bg-orange-100'}`}>
                <DollarSign className={`h-5 w-5 ${netTotal >= 0 ? 'text-sky-600' : 'text-orange-600'}`} />
              </div>
              <span className="text-sm font-medium text-slate-600">Net Total</span>
            </div>
          </div>
          <p className={`text-3xl font-bold ${netTotal >= 0 ? 'text-sky-600' : 'text-orange-600'}`} data-testid="net-total">
            {formatCurrency(netTotal)}
          </p>
          {/* Calculation Mode Toggle */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <Button
                variant={netCalculationMode === 'subtract' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCalculationModeChange('subtract')}
                className={`text-xs ${netCalculationMode === 'subtract' ? 'bg-slate-800' : ''}`}
                data-testid="mode-subtract"
              >
                Commission - Expenses
              </Button>
              <Button
                variant={netCalculationMode === 'add' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCalculationModeChange('add')}
                className={`text-xs ${netCalculationMode === 'add' ? 'bg-slate-800' : ''}`}
                data-testid="mode-add"
              >
                Commission + Expenses
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-red-800">Expenses ({filteredExpenses.length})</h3>
          </div>
          <Button
            size="sm"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-red-600 hover:bg-red-700"
            data-testid="add-expense-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Add Expense</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full" data-testid="expenses-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Driver</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Vehicle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Receipt</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    {searchQuery ? 'No expenses match your search' : 'No expenses found for this month'}
                  </td>
                </tr>
              ) : (
                filteredExpenses.map(expense => (
                  <tr key={expense.id} className="hover:bg-slate-50" data-testid={`expense-row-${expense.id}`}>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(expense.date)}</td>
                    <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                      {expense.driver_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {expense.vehicle_plate || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        {expense.reason || '-'}
                        {(expense.has_receipt || expense.receipt_filename) && <FileText className="h-4 w-4 text-slate-400" />}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(expense.has_receipt || expense.receipt_filename) ? (
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewReceipt(expense)}
                            className="text-blue-600 hover:text-blue-700 h-8 px-2"
                            title="View receipt"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => downloadReceipt(expense)}
                            className="text-emerald-600 hover:text-emerald-700 h-8 px-2"
                            title="Download receipt"
                            data-testid={`download-expense-${expense.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-red-600 text-right">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditDialog(expense)}
                          className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-sm"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setExpenseToDelete(expense);
                            setDeleteDialogOpen(true);
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden p-4 space-y-3">
          {filteredExpenses.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              {searchQuery ? 'No expenses match your search' : 'No expenses found for this month'}
            </div>
          ) : (
            filteredExpenses.map(expense => (
              <div key={expense.id} className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{expense.driver_name || 'N/A'}</p>
                    <p className="text-sm text-slate-500">{formatDate(expense.date)}</p>
                  </div>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(expense.amount)}</p>
                </div>
                
                <div className="text-sm text-slate-600">
                  <p><span className="text-slate-400">Reason:</span> {expense.reason || '-'}</p>
                  {expense.vehicle_plate && (
                    <p><span className="text-slate-400">Vehicle:</span> {expense.vehicle_plate}</p>
                  )}
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    {(expense.has_receipt || expense.receipt_filename) && (
                      <>
                        <button
                          onClick={() => viewReceipt(expense)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => downloadReceipt(expense)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditDialog(expense)}
                      className="p-2 text-slate-600 hover:bg-slate-200 rounded-lg"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setExpenseToDelete(expense);
                        setDeleteDialogOpen(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Commissions Table - Linked to Rides */}
      <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm shadow-sm overflow-hidden mt-6 lg:mt-8">
        <div className="px-4 lg:px-6 py-3 lg:py-4 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h3 className="font-semibold text-emerald-800">Commissions ({filteredCommissions.length})</h3>
          </div>
          <p className="text-sm text-emerald-600 font-medium">
            {formatCurrency(filteredCommissions.reduce((sum, r) => sum + (r.driver_commission || 0), 0))}
          </p>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ride Reference</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Route</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Assigned Driver</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Commission To</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ride Price</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Commission</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredCommissions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-slate-500">
                    {searchQuery ? 'No commissions match your search' : 'No commissions found for this period'}
                  </td>
                </tr>
              ) : (
                filteredCommissions.map(ride => (
                  <tr key={ride.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {ride.date_time ? formatDateTime(ride.date_time) : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/admin/rides?edit=${ride.id}`)}
                        className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        title="Click to view/edit ride"
                        data-testid={`ride-ref-${ride.id}`}
                      >
                        #{ride.id.substring(0, 8)}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                      {ride.customer_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-green-500" />
                        <span className="truncate max-w-[100px]" title={ride.pickup_location}>
                          {ride.pickup_location || 'N/A'}
                        </span>
                        <span className="text-slate-400">→</span>
                        <MapPin className="h-3 w-3 text-red-500" />
                        <span className="truncate max-w-[100px]" title={ride.dropoff_location}>
                          {ride.dropoff_location || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-slate-400" />
                        {ride.assigned_driver_name || 'Unassigned'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {/* Show commission recipient - highlight if different from assigned driver */}
                      {ride.commission_driver_id && ride.commission_driver_id !== ride.assigned_driver_id ? (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-emerald-500" />
                          <span className="text-emerald-700 font-medium">
                            {ride.commission_driver_name || 'Unknown'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-slate-500">
                          <span className="text-xs">Same as assigned</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      €{(ride.price || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-emerald-600 text-right">
                      {formatCurrency(ride.driver_commission)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/rides?edit=${ride.id}`)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid={`view-ride-${ride.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Ride
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden p-4 space-y-3">
          {filteredCommissions.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              {searchQuery ? 'No commissions match your search' : 'No commissions found for this period'}
            </div>
          ) : (
            filteredCommissions.map(ride => (
              <div key={ride.id} className="bg-emerald-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{ride.customer_name || 'N/A'}</p>
                    <p className="text-sm text-slate-500">{ride.date_time ? formatDateTime(ride.date_time) : 'N/A'}</p>
                  </div>
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(ride.driver_commission)}</p>
                </div>
                
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-3 w-3 text-green-500" />
                    <span className="truncate">{ride.pickup_location || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-3 w-3 text-red-500" />
                    <span className="truncate">{ride.dropoff_location || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 pt-2 border-t border-emerald-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Assigned Driver:</span>
                    <span className="text-slate-600">{ride.assigned_driver_name || 'Unassigned'}</span>
                  </div>
                  {/* Show commission recipient if different */}
                  {ride.commission_driver_id && ride.commission_driver_id !== ride.assigned_driver_id && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-600 font-medium">Commission To:</span>
                      <span className="text-emerald-700 font-medium">{ride.commission_driver_name || 'Unknown'}</span>
                    </div>
                  )}
                  <div className="flex justify-end mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/rides?edit=${ride.id}`)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50 h-9"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update the expense details below.' : 'Enter the details for the new expense.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount (€)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="expense-amount-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="driver_id">Driver</Label>
                <Select
                  value={formData.driver_id}
                  onValueChange={(value) => setFormData({ ...formData, driver_id: value })}
                >
                  <SelectTrigger data-testid="expense-driver-select">
                    <SelectValue placeholder="Select driver (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vehicle_id">Vehicle</Label>
                <Select
                  value={formData.vehicle_id}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                >
                  <SelectTrigger data-testid="expense-vehicle-select">
                    <SelectValue placeholder="Select vehicle (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map(vehicle => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.plate_number} {vehicle.model && `- ${vehicle.model}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Fuel, tolls, parking, etc..."
                rows={2}
                data-testid="expense-reason-input"
              />
            </div>

            <div>
              <Label>Receipt Photo (Optional)</Label>
              {formData.receipt_filename ? (
                <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-sm mt-1">
                  <FileImage className="h-5 w-5 text-slate-500" />
                  <span className="flex-1 text-sm text-slate-700 truncate">{formData.receipt_filename}</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, receipt_data: null, receipt_filename: null })}
                    className="p-1 text-slate-500 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-1">
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                  />
                  <p className="text-xs text-slate-500 mt-1">Max 5MB. Upload an image or PDF of the receipt.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="save-expense-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingExpense ? 'Update' : 'Add'} Expense
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Receipt</span>
              {selectedReceipt && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadFile(selectedReceipt.data, selectedReceipt.filename || 'receipt')}
                  className="flex items-center gap-1"
                  data-testid="download-modal-receipt"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <div className="flex flex-col items-center gap-4">
              <img 
                src={selectedReceipt.data} 
                alt="Receipt" 
                className="max-h-[60vh] object-contain rounded-lg"
              />
              {selectedReceipt.filename && (
                <p className="text-sm text-slate-500">{selectedReceipt.filename}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
